import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Button,
  Accordion,
  Container,
  Row,
  Col,
  Form,
} from "react-bootstrap";
import { useParams } from "react-router-dom";

import { AUTH_ROLES } from "api/common/types/auth.types";
import ECRCommentsTable from "components/ECR/ECRCommentsTable";
import Notes from "components/Notes/Notes";
import TestedFeature from "components/TestedFeature/TestedFeature";
import TechnicalQueriesTable from "components/TRRTS/TRRTSManager/TechnicalQueriesTable";
import HTMLEditor from "components/UI/HTMLEditor";
import { useAuth } from "hooks/useAuth";
import { useInterval } from "hooks/useInterval";

import AMHistoryTable from "./AMHistoryTable";
import EcrCommentsCount from "./EcrCommentsCount";

import "./ProjectDetailsAccordion.css";
import "../../Products.css";
import EARDetailsAccordion from "features/memberPortal/EAR/components/EarAccordion/EarDetailsAccordion";

const PRODUCT_DESCRIPTION = 0;
const SECURITY_EVALUATION_SUMMARY = 1;
const ENVIRONMENTAL_STRENGTHS = 2;
const EVALUATION_CONFIGURATION = 3;

export default function PostingAccordion({
  project,
  refetch,
  readyToPostChecks,
  handleReadyToPostCheck,
}) {
  const { id } = useParams();
  const [activeKey, setActiveKey] = useState([
    PRODUCT_DESCRIPTION,
    SECURITY_EVALUATION_SUMMARY,
    ENVIRONMENTAL_STRENGTHS,
    EVALUATION_CONFIGURATION,
  ]);
  const [editedHtmlFields, setEditedHtmlFields] = useState({});
  const [showAlert, setShowAlert] = useState("");
  const [ecrCounts, setEcrCounts] = useState({});
  const { authToken, csrfToken, permissions } = useAuth();

  const isLab = permissions.role_type === AUTH_ROLES.LAB;
  const isValidator = permissions.role_type === AUTH_ROLES.VALIDATOR;
  const projectIsSigned = !!project.signature;
  const isLabAndSignedOrIsValidator = (isLab && projectIsSigned) || isValidator;

  const fetchEcrCounts = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}ecr/count_comments?vid=${project.v_id}`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        },
      )
      .then((response) => {
        setEcrCounts(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [project]);

  useEffect(() => {
    fetchEcrCounts();
  }, [fetchEcrCounts]);

  useInterval(() => {
    fetchEcrCounts();
  }, 180000); //3 min

  const expandAll = () => {
    const allKeys = [];
    projectDescriptionFields.forEach((pd, idx) => {
      allKeys.push(idx);
    });
    setActiveKey(allKeys);
  };

  const collapseAll = () => {
    setActiveKey([]);
  };

  const handleItemClick = (idx) => {
    let newKeys = [...activeKey];
    if (newKeys.includes(idx)) {
      newKeys = newKeys.filter((key) => key !== idx);
    } else {
      newKeys.push(idx);
    }
    setActiveKey(newKeys);
  };

  const handleChange = (e) => {
    if (e.target.type === "checkbox") {
      handleReadyToPostCheck({ [e.target.name]: e.target.checked });
    } else {
      const { name, value } = e.target;
      const startingValue = project[name];

      if (startingValue === value) return;

      setEditedHtmlFields((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSaveAll = () => {
    axios
      .put(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/${project.product_id}/`,
        editedHtmlFields,
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then(() => {
        setEditedHtmlFields({});
        setShowAlert("success");
        refetch();
      })
      .catch(() => setShowAlert("fail"));
  };

  let projectDescriptionFields = [
    {
      title: "Product Description",
      name: "product_description",
      text: project?.product_description,
    },
    {
      title: "Security Evaluation Summary",
      name: "security_evaluation_summary",
      text: project?.security_evaluation_summary,
    },
    {
      title: "Environmental Strengths",
      name: "environmental_strengths",
      text: project?.environmental_strengths,
    },
    {
      title: "Evaluation Configuration",
      name: "evaluation_configuration",
      text: project?.evaluation_configuration,
    },
    {
      title: "Tested Features",
    },
    { title: "EAR Review" },
    { title: "Assurance Maintenance History" },
    { title: "Technical Queries" },
    {
      title: "ECR Comments",
    },
  ];

  return (
    <Container>
      <Row>
        <Col>
          {Object.keys(editedHtmlFields).length > 0 && showAlert === "" && (
            <Alert variant="danger">Please Save Changes!</Alert>
          )}
          {showAlert === "success" && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setShowAlert("")}
            >
              Changes successfully saved!
            </Alert>
          )}
          {showAlert === "fail" && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setShowAlert("")}
            >
              Changes failed to save!
            </Alert>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          {activeKey.length !== projectDescriptionFields.length && (
            <Button
              variant="link"
              onClick={expandAll}
              className="text-primary ml-auto"
            >
              Expand All
            </Button>
          )}
          {activeKey.length === projectDescriptionFields.length && (
            <Button
              variant="link"
              onClick={collapseAll}
              className="text-primary ml-auto"
            >
              Collapse All
            </Button>
          )}
        </Col>
        <Col className="d-flex justify-content-end mb-2">
          {Object.keys(editedHtmlFields).length > 0 && (
            <Button variant="success" onClick={handleSaveAll}>
              Save All
            </Button>
          )}
        </Col>
      </Row>
      <Accordion activeKey={activeKey} alwaysOpen>
        {projectDescriptionFields.map((pd, idx) => (
          <Accordion.Item
            eventKey={idx}
            key={idx}
            className={pd.title !== "ECR Comments" ? "border-bottom-dark" : ""}
          >
            <Accordion.Header onClick={() => handleItemClick(idx)}>
              <Container fluid>
                <Row>
                  <Col xs={12} sm={3}>
                    <h6 className="mb-2 mb-lg-0">{pd.title}</h6>
                  </Col>
                  {pd.title === "ECR Comments" && (
                    <EcrCommentsCount ecrCounts={ecrCounts} />
                  )}
                </Row>
              </Container>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <Container>
                <Row>
                  <Col>
                    {![
                      "ECR Comments",
                      "EAR Review",
                      "Tested Features",
                      "Assurance Maintenance History",
                      "Technical Queries",
                    ]?.includes(pd.title) && (
                      <Row>
                        <Col className="d-flex justify-content-end mb-2">
                          {(() => {
                            const checked = readyToPostChecks?.find(
                              (check) => `ready-to-post-${idx}` in check,
                            )?.[`ready-to-post-${idx}`];
                            return (
                              <Form.Check
                                type={"checkbox"}
                                name={`ready-to-post-${idx}`}
                                id={`ready-to-post-${idx}`}
                                label="Reviewed"
                                checked={checked}
                                onChange={handleChange}
                                style={{
                                  color: checked ? "green" : "red",
                                  fontWeight: "bold",
                                }}
                                className={
                                  checked
                                    ? "text-success fw-bold"
                                    : "text-danger fw-bold"
                                }
                              />
                            );
                          })()}
                        </Col>
                      </Row>
                    )}
                    <Row className="mb-2">
                      {pd.title === "EAR Review" && (
                        <EARDetailsAccordion id={id} />
                      )}
                      {pd.title === "Assurance Maintenance History" && (
                        <AMHistoryTable
                          id={id}
                          product={project}
                          fetchProject={refetch}
                        />
                      )}
                      {pd.title === "Tested Features" && project.product_id && (
                        <TestedFeature
                          featureType="product"
                          featureId={project.product_id}
                          readOnly={isLabAndSignedOrIsValidator}
                        />
                      )}
                      {pd.title === "Technical Queries" && (
                        <TechnicalQueriesTable id={id} />
                      )}
                      {pd.title === "ECR Comments" && project.v_id && (
                        <ECRCommentsTable
                          vidNumber={project.v_id}
                          fetchEcrCounts={fetchEcrCounts}
                        />
                      )}
                      {![
                        "ECR Comments",
                        "EAR Review",
                        "Tested Features",
                        "Assurance Maintenance History",
                        "Technical Queries",
                      ]?.includes(pd.title) && (
                        <HTMLEditor
                          name={pd.name}
                          handleChange={handleChange}
                          setIsValid={() => {}}
                          isValid={true}
                          defaultValue={pd.text}
                        />
                      )}
                    </Row>
                  </Col>
                </Row>
              </Container>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
      <Notes item={project} note_type="product" id={id} />
    </Container>
  );
}
